import { createFileRoute } from "@tanstack/react-router"
import { useT } from "@/lib/i18n"
import { Button, Spinner, Toast } from "@/components/ui"
import { PlanCards } from "@/components/PlanCards"
import { PricingFaq } from "@/components/PricingFaq"
import { IntervalToggle, PublicPlanCta, TrialRibbon, usePricing } from "@/components/Pricing"
import { SiteFooter, SiteHeader } from "@/components/SiteChrome"

export const Route = createFileRoute("/pricing")({ component: PricingPage })

function PricingPage() {
  const t = useT()
  const p = usePricing()
  const current = p.signedIn ? p.me?.tier ?? null : null
  const canBuy = p.signedIn && (current === "free" || current == null)

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto w-full max-w-5xl px-4 pb-8 pt-6 md:pt-10">
        <div className="flex flex-col items-start gap-4 md:items-center md:text-center">
          <TrialRibbon days={p.trialDays} />
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t("pricing.title")}</h1>
          <p className="max-w-xl text-[15px] text-muted-foreground">{t("pricing.sub")}</p>
          <IntervalToggle value={p.interval} onChange={p.setInterval} />
        </div>

        {p.err && <Toast variant="destructive">{p.err}</Toast>}

        <div className="mt-8">
          {p.plans.isLoading && <div className="flex justify-center py-10"><Spinner /></div>}
          {p.plans.error && <Toast variant="destructive">{(p.plans.error as Error).message}</Toast>}
          {p.plans.data && (
            <PlanCards
              plans={p.plans.data.plans}
              current={current}
              interval={p.interval}
              busy={p.busy}
              proNote={p.trialDays > 0 ? t("pricing.trialRibbon", { days: p.trialDays }) : undefined}
              onChoose={canBuy ? p.choose : undefined}
              renderCta={p.signedIn
                ? (plan) => (plan.tier === current
                  ? <Button variant="outline" className="w-full" disabled>{t("pricing.ctaCurrent")}</Button>
                  : null)
                : (plan) => <PublicPlanCta plan={plan} signupMode={p.signupMode} />}
            />
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          {t("pricing.vat")}{p.trialDays > 0 ? ` ${t("pricing.trialNote")}` : ""}
        </p>

        <PricingFaq className="mt-14" />
      </main>
      <SiteFooter />
    </div>
  )
}
